import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { displayOrganizationNumber } from '@financialharmony/tax-se';
import { formatCurrency } from '@/utils/paymentUtils';
import { TaxAccountApiClient, type TaxAccountDetail } from '@/clients/taxAccountApiClient';
import { taxRowActions, taxRowReason } from '@financialharmony/ui/tax/rowModel';
import TaxCheckAgain from '@/components/bank/TaxCheckAgain';
import { TaxBookkeepingStatusCell } from '@/components/tax/TaxBookkeepingStatusCell';
import { useFormatOptionalDate } from '@/i18n/useFormatOptionalDate';
import { useTaxAccountReasons } from '@/i18n/useTaxAccountReasons';
import { useTaxNoAccountDetail } from '@/i18n/useTaxNoAccountDetail';
import { useTaxTransactionLabel } from '@/i18n/useTaxTransactionLabel';

/**
 * Consultant tax account detail (FIN-595) — "are they square with Skatteverket, and why".
 *
 * Mirrors `src/pages/BankAccountDetails.tsx`: back link, an overview Card, then a Card wrapping
 * a `<Table>`. Two things from that file are deliberately NOT copied — it colours amounts with
 * raw `text-green-600`/`text-red-600`, which `.claude/rules/design-system.md` bans, and its
 * transactions are `mockTransactions`. Semantic tokens and real data here.
 *
 * The balance is the authority's own saldo and nothing else. An earlier draft put the BAS 1630
 * ledger balance beside it with a reconciliation badge; that was dropped deliberately. "Are they
 * square with Skatteverket" is answered by the saldo alone, while 1630 answers whether our books
 * have caught up — and journaling lagging is normal, so the badge would have been lit almost
 * permanently. FIN-770 compares the transaction feed against the saldo in the backend instead,
 * where a real discrepancy is a system error rather than a second number to arbitrate.
 */

/**
 * An amount the server withheld (FIN-1167 D10: the owner's private tax) reads as Private, never
 * as a formatted figure — `formatCurrency(Number(null))` would print a plausible "0 kr".
 */
function AmountCell({ item }: { item: { amount: string | null; amountWithheld?: boolean; currency: string } }) {
  const t = useTranslations('consultantTax.detail');
  if (item.amountWithheld || item.amount === null) {
    return (
      <span className="text-muted-foreground" data-testid="tax-detail-amount-private">
        {t('private')}
      </span>
    );
  }
  return <>{formatCurrency(Number(item.amount), item.currency)}</>;
}

const TaxAccountDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const t = useTranslations('consultantTax.detail');
  const formatOptionalDate = useFormatOptionalDate();
  const transactionLabel = useTaxTransactionLabel();
  const reasons = useTaxAccountReasons();
  const taxNoAccountDetail = useTaxNoAccountDetail();
  const tNoAccount = useTranslations('tax.noAccount');
  const [detail, setDetail] = useState<TaxAccountDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      setDetail(await TaxAccountApiClient.detail(id));
    } catch {
      // An empty page would read as "nothing here", which is a different and wrong claim.
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** The line under the figure: a final or last-known figure never reads as a current one. */
  const asOfLine = (date: string, status: string, authority: string): string => {
    if (status === 'closed') return t('asOfClosed', { date });
    if (status === 'revoked') return t('asOfRevoked', { date });
    return t('asOfCurrent', { authority, date });
  };

  // After a Check again: re-read in place, without the loading screen that would unmount the
  // button and the sentence it just answered with.
  const reloadInPlace = useCallback(async () => {
    if (!id) return;
    setDetail(await TaxAccountApiClient.detail(id));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-muted-foreground" data-testid="tax-detail-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('loading')}
      </div>
    );
  }

  if (loadFailed || !detail) {
    return (
      <div className="p-6">
        <Alert variant="destructive" data-testid="tax-detail-error">
          <AlertDescription>
            {t('loadFailed')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { account, transactions, upcoming } = detail;
  const rawReason = taxRowReason(account.balanceUnavailableReason, detail.grantOutcome) ?? 'not_connected';
  const closed = account.status === 'closed';
  const accessLost = account.status === 'revoked';
  // A closed or lost account is never "waiting for a skattekonto", whatever the reason says.
  let reason = rawReason;
  if (rawReason === 'no_tax_account' && closed) reason = 'closed';
  if (rawReason === 'no_tax_account' && accessLost) reason = 'revoked';
  const checksLine = reason === 'no_tax_account' ? taxNoAccountDetail(account) : null;
  const canCheckAgain = taxRowActions(reason, false, account.activating === true).checkAgain;
  const authority = account.authorityName;
  const copy = reasons.badge(authority);
  const explanation = reasons.explanation(authority);

  return (
    <div className="space-y-6 p-6" data-testid="tax-account-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="tax-detail-back">
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('back')}
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
          <p className="text-sm text-muted-foreground">
            {authority} · {displayOrganizationNumber(account.registrationNumber)}
          </p>
        </div>
        <Badge variant={account.status === 'active' ? 'default' : 'secondary'} data-testid="tax-detail-status">
          {account.status === 'active' ? t('connected') : copy[reason]}
        </Badge>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('balanceTitle', { authority })}
          </div>

          {account.balance ? (
            <>
              {/* Signed, never relabelled — the authority reports negative when they are owed. */}
              <p
                className={closed || accessLost ? 'text-3xl font-bold tabular-nums text-muted-foreground' : 'text-3xl font-bold tabular-nums'}
                data-testid="tax-detail-balance"
              >
                {formatCurrency(Number(account.balance.amount), account.balance.currency)}
              </p>
              <p className="mt-1 text-sm text-muted-foreground" data-testid="tax-detail-as-of">
                {asOfLine(account.balance.asOfDate, account.status, authority)}
              </p>
              {account.balanceNotice === 'vanished' && (
                <p className="mt-1 text-sm text-muted-foreground" data-testid="tax-detail-vanished">
                  {tNoAccount('vanished', { authority })}
                </p>
              )}
            </>
          ) : (
            <>
              {/* Deliberately no figure. A plausible-looking number is worse than none. */}
              <p className="text-3xl font-bold text-muted-foreground" data-testid="tax-detail-balance-withheld">
                —
              </p>
              {/* Closed and access lost are explained once, below — this alert would only repeat them. */}
              {!closed && !accessLost && (
                <Alert className="mt-3" data-testid="tax-detail-unavailable">
                  <AlertDescription>
                    <span className="font-medium">{copy[reason].replace(/\.$/, '')}.</span>{' '}
                    {explanation[reason]}
                    {checksLine && (
                      <div className="mt-2 text-xs text-muted-foreground" data-testid="tax-detail-no-account-checks">
                        {checksLine}
                      </div>
                    )}
                    {canCheckAgain && (
                      <div className="mt-3">
                        <TaxCheckAgain
                          accountId={account.id}
                          authorityName={authority}
                          onChecked={reloadInPlace}
                          testIdPrefix="tax-detail"
                        />
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {closed && (
            <Alert className="mt-3" data-testid="tax-detail-closed">
              <AlertDescription>
                <span className="font-medium">{copy.closed}.</span> {explanation.closed}
                {/* No claim about why: never read, or withheld — neither is a zero. */}
                {!account.balance && ` ${t('noFinalBalance')}`}
              </AlertDescription>
            </Alert>
          )}

          {accessLost && (
            <Alert className="mt-3" data-testid="tax-detail-access-lost">
              <AlertDescription>
                <span className="font-medium">{copy.revoked}.</span> {explanation.revoked}
                <div className="mt-3">
                  <Button
                    size="sm"
                    data-testid="tax-detail-reconnect"
                    // The Bank page owns the one connect dialog; arriving with this state opens it.
                    onClick={() => navigate('/bank', { state: { reconnectTax: true } })}
                  >
                    {t('reconnect')}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {account.enforcement && (
            <div
              className="mt-4 flex flex-wrap items-baseline gap-4 rounded-md bg-muted px-4 py-3"
              data-testid="tax-detail-enforcement"
            >
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('enforcementTitle')}
                </div>
                <div className="text-lg font-semibold tabular-nums" data-testid="tax-detail-enforcement-amount">
                  {formatCurrency(Number(account.enforcement.amount), account.enforcement.currency)}
                </div>
              </div>
              {/*
                Deliberately not a total, and deliberately not reassuring. The figure is shown
                so a consultant knows the debt exists, but nothing in this product collects,
                books or tracks it — saying so is the difference between informing them and
                implying we have it covered.
              */}
              <p className="text-xs text-muted-foreground" data-testid="tax-detail-enforcement-note">
                {t.rich('enforcementNote', {
                  emphasis: (chunks) => <span className="font-medium">{chunks}</span>,
                })}
              </p>
            </div>
          )}

        </CardContent>
      </Card>

      {upcoming.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('upcoming')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table data-testid="tax-detail-upcoming">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('due')}</TableHead>
                  <TableHead>{t('what')}</TableHead>
                  <TableHead className="text-right">{t('amount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcoming.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.dueDate ? formatOptionalDate(item.dueDate) : (
                        // The authority's own omission (forfallodatum is optional), stated as
                        // such — never a stand-in date, which would be a deadline nobody set.
                        <span className="text-muted-foreground" data-testid="tax-detail-upcoming-due-missing">
                          {t('dueMissing', { authority })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{transactionLabel(item.transactionType, item.rawLabel)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <AmountCell item={item} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base">{t('transactions')}</CardTitle>
          {/* Provenance stated once, not repeated on every row. */}
          <span className="text-xs font-medium text-muted-foreground" data-testid="tax-detail-source">
            {t('source', { authority })}
          </span>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="tax-detail-no-transactions">
              {t('noTransactions')}
            </p>
          ) : (
            <Table data-testid="tax-detail-transactions">
              <TableHeader>
                {/*
                  No "Period" and no "Decision" column: Skatteverket sends neither on a settled
                  row (beslutsdatum arrives on announced items only — FIN-729). The interest date
                  (ranteberakningsdatum) is on every row and can differ from the booking date,
                  e.g. a payment earns interest from the day it arrived.
                */}
                <TableRow>
                  <TableHead>{t('booked')}</TableHead>
                  <TableHead>{t('interestDate')}</TableHead>
                  <TableHead>{t('what')}</TableHead>
                  <TableHead className="text-right">{t('amount')}</TableHead>
                  <TableHead>{t('bookkeeping')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>{formatOptionalDate(transaction.bookingDate, '—')}</TableCell>
                    <TableCell>{formatOptionalDate(transaction.interestDate, '—')}</TableCell>
                    <TableCell>
                      <div>{transactionLabel(transaction.transactionType, transaction.rawLabel)}</div>
                      {/*
                        The authority's own wording, on every row — not just unrecognised ones.
                        The type above is derived by prefix-matching 13 patterns against free
                        text (Skatteverket sends no type code at all), so a mis-mapping is a
                        real possibility and this line is the only way a consultant can spot
                        one. Deliberately `rawLabel` rather than `description`: they happen to
                        be the same field for Skatteverket, so rendering `description` would
                        hide the evidence the moment a provider supplies a separate one.
                      */}
                      {transaction.rawLabel && (
                        <div className="text-xs text-muted-foreground">{transaction.rawLabel}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <AmountCell item={transaction} />
                    </TableCell>
                    <TableCell>
                      <TaxBookkeepingStatusCell
                        transactionId={transaction.id}
                        bookkeeping={transaction.bookkeeping}
                        onResolved={reloadInPlace}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxAccountDetails;
